import { Layout, useLayoutContext } from "@overmap-ai/blocks"
import { Outlet, useMatches } from "@tanstack/react-router"
import { cx } from "class-variance-authority"
import { Fragment, memo, useMemo } from "react"

import { useViewportSize } from "../contexts/hooks"
import { AiChatPanel } from "../slop/AiChatPanel"
import { aiSlideOutWidth, defaultOpen, rightSlideOutWidth, SLIDE_OUT_IDS } from "./constants"
import {
	layoutSlideOutContent,
	layoutSlideOutHandle,
	layoutSlideOutOverlay,
	layoutSlideOutRoot,
	layoutSlideOutViewport,
} from "./cva"

export const AppLayout = memo(() => {
	const { size } = useViewportSize()
	const { hideLayout } = useLayoutContext()

	const matches = useMatches()

	const {
		topBar: TopBar = Fragment,
		leftSlideOut: LeftSlideOut = Fragment,
		rightSlideOut: RightSlideOut = Fragment,
		hideLeftSlideOut,
		hideRightSlideOut = true,
	} = useMemo(() => {
		return matches[matches.length - 1].staticData
	}, [matches])

	return (
		<div className="pointer-events-none relative h-svh w-svw overflow-hidden bg-transparent">
			<Layout.SlideOutRoot
				slideOutId={SLIDE_OUT_IDS.LEFT}
				className={cx("pointer-events-none", layoutSlideOutRoot())}
				hide={hideLayout || hideLeftSlideOut}
				defaultOpen={defaultOpen}
			>
				<Layout.SlideOutOverlay
					className={cx("pointer-events-auto z-[1005]", layoutSlideOutOverlay())}
					smallModeOnly
				/>

				<Layout.SlideOutContent
					className={cx(
						"pointer-events-auto z-[1006]",
						layoutSlideOutContent({
							side: "left",
						}),
					)}
					side="left"
					initialSize="230px"
					minSize="220px"
					resizeable
				>
					<LeftSlideOut />
					<Layout.SlideOutHandle className={layoutSlideOutHandle({ side: "left" })} />
				</Layout.SlideOutContent>

				<Layout.SlideOutViewport className={cx("pointer-events-none", layoutSlideOutViewport())}>
					<Layout.SlideOutRoot
						className={cx("pointer-events-none", layoutSlideOutRoot())}
						slideOutId={SLIDE_OUT_IDS.AI}
					>
						<Layout.SlideOutViewport
							className={cx("pointer-events-none flex-col", layoutSlideOutViewport())}
						>
							<Layout.Container className="pointer-events-auto relative z-[1002] h-max shrink-0">
								<TopBar />
							</Layout.Container>

							<Layout.SlideOutRoot
								className={cx("pointer-events-none", layoutSlideOutRoot())}
								slideOutId={SLIDE_OUT_IDS.RIGHT}
								hide={hideLayout || hideRightSlideOut}
							>
								<Layout.SlideOutViewport
									className={cx(
										"pointer-events-none *:pointer-events-auto",
										layoutSlideOutViewport(),
									)}
								>
									<Outlet />
								</Layout.SlideOutViewport>

								<Layout.SlideOutOverlay
									className={cx("pointer-events-auto z-[1000]", layoutSlideOutOverlay())}
									smallModeOnly
								/>

								<Layout.SlideOutContent
									className={cx(
										"pointer-events-auto z-[1001]",
										layoutSlideOutContent({
											side: "right",
										}),
									)}
									side="right"
									initialSize={rightSlideOutWidth(size)}
									minSize={rightSlideOutWidth(size)}
									maxSize="90%"
									resizeable
								>
									<RightSlideOut />
									<Layout.SlideOutHandle className={layoutSlideOutHandle({ side: "right" })} />
								</Layout.SlideOutContent>
							</Layout.SlideOutRoot>
						</Layout.SlideOutViewport>

						<Layout.SlideOutOverlay
							className={cx("pointer-events-auto z-[1003]", layoutSlideOutOverlay())}
							smallModeOnly
						/>

						<Layout.SlideOutContent
							className={cx(
								"pointer-events-auto z-[1004]",
								layoutSlideOutContent({
									side: "right",
								}),
							)}
							side="right"
							initialSize={aiSlideOutWidth(size)}
							minSize={aiSlideOutWidth(size)}
							maxSize="100%"
							resizeable
						>
							<AiChatPanel />
							<Layout.SlideOutHandle className={layoutSlideOutHandle({ side: "right" })} />
						</Layout.SlideOutContent>
					</Layout.SlideOutRoot>
				</Layout.SlideOutViewport>
			</Layout.SlideOutRoot>
		</div>
	)
})
AppLayout.displayName = "AppLayout"
